import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { schoolLogoAPI } from '../services/api';

const normalizeApiBase = (value) => {
  const trimmed = (value || '/api').replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};
const API = normalizeApiBase(process.env.REACT_APP_API_URL);

// ── Veda-style grade (A1/A2/B1/B2/C1/C2/D/E based on percentage) ─────────────
function vedaGrade(pct) {
  if (pct >= 91) return { grade: 'A1', gpa: 10 };
  if (pct >= 81) return { grade: 'A2', gpa: 9 };
  if (pct >= 71) return { grade: 'B1', gpa: 8 };
  if (pct >= 61) return { grade: 'B2', gpa: 7 };
  if (pct >= 51) return { grade: 'C1', gpa: 6 };
  if (pct >= 41) return { grade: 'C2', gpa: 5 };
  if (pct >= 35) return { grade: 'D',  gpa: 4 };
  return           { grade: 'E',  gpa: 3 };
}

// ── Router ────────────────────────────────────────────────────────────────────
export default function ProgressCard() {
  const parts = window.location.pathname.split('/');
  const pcIdx = parts.indexOf('progress-card');
  const schoolCode = pcIdx >= 0 ? decodeURIComponent(parts[pcIdx + 1] || '') : '';
  const studentIdFromUrl = pcIdx >= 0 ? parts[pcIdx + 2] : undefined;
  if (!studentIdFromUrl) return <LookupPage schoolCode={schoolCode} />;
  if (studentIdFromUrl === 'print-all') return <AllProgressCards schoolCode={schoolCode} />;
  return <StudentProgressCard schoolCode={schoolCode} studentId={studentIdFromUrl} />;
}

// ── AllProgressCards ──────────────────────────────────────────────────────────
function AllProgressCards({ schoolCode }) {
  const params = new URLSearchParams(window.location.search);
  const ids = (params.get('ids') || '').split(',').map(s => s.trim()).filter(Boolean);
  if (!ids.length) return <div style={S.center}><p style={{ fontSize: 18 }}>No student IDs provided.</p></div>;
  return (
    <div>
      <style>{`@media print { .pc-all-toolbar{display:none!important} .pc-card-break{page-break-after:always;break-after:page} .pc-no-print{display:none!important} }`}</style>
      <div className="pc-all-toolbar" style={{ padding:'12px 24px', background:'#1e3a6e', color:'#fff', display:'flex', alignItems:'center', gap:20, position:'sticky', top:0, zIndex:100 }}>
        <strong>{ids.length} Progress Cards</strong>
        <button onClick={() => window.print()} style={{ padding:'7px 20px', background:'#fff', color:'#1e3a6e', border:'none', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:14 }}>Print All</button>
        <span style={{ fontSize:13, opacity:0.8 }}>Wait for all cards to load, then click Print All</span>
      </div>
      {ids.map((id, idx) => (
        <div key={id} className={idx < ids.length - 1 ? 'pc-card-break' : ''}>
          <StudentProgressCard schoolCode={schoolCode} studentId={id} />
        </div>
      ))}
    </div>
  );
}

// ── SchoolLogo ────────────────────────────────────────────────────────────────
function SchoolLogo({ logoUrl, style }) {
  const src = schoolLogoAPI.resolveUrl(logoUrl || '');
  if (!src) return null;
  return <img src={src} alt="School Logo" style={style} onError={e => { e.currentTarget.style.display = 'none'; }} />;
}

// ── LookupPage ────────────────────────────────────────────────────────────────
function LookupPage({ schoolCode }) {
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [schoolMeta, setSchoolMeta] = useState(null);

  useEffect(() => {
    if (!schoolCode) return;
    axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/meta`)
      .then(res => { if (res.data.success) setSchoolMeta(res.data.data); })
      .catch(() => {});
  }, [schoolCode]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const clean = mobile.replace(/\D/g, '');
    if (clean.length < 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    setLoading(true); setError(''); setResults(null);
    try {
      const res = await axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/lookup?mobile=${clean}`);
      if (res.data.success) {
        setResults(res.data.students);
        if (res.data.students.length === 1) {
          window.location.href = `/progress-card/${encodeURIComponent(schoolCode)}/${res.data.students[0].student_id}`;
        }
      } else setError(res.data.message || 'No students found');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to search. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <style>{printStyles}</style>
      <div style={{ ...S.card, maxWidth: 480 }}>
        <div style={S.header}>
          {schoolMeta?.school_logo_url && <SchoolLogo logoUrl={schoolMeta.school_logo_url} style={S.headerLogo} />}
          <h1 style={S.schoolName}>{schoolMeta?.school_name || schoolCode}</h1>
          {schoolMeta?.school_tagline && <p style={S.tagline}>{schoolMeta.school_tagline}</p>}
          {schoolMeta?.school_location && <p style={S.location}>&#x1F4CD; {schoolMeta.school_location}</p>}
          <div style={S.cardTitleBanner}><span style={S.cardTitleText}>STUDENT PROGRESS CARD</span></div>
        </div>
        <div style={{ padding: '28px 30px' }}>
          <p style={{ textAlign:'center', color:'#4a5568', fontSize:14, marginBottom:24, lineHeight:1.7 }}>
            మీ పిల్లల Progress Card చూడటానికి<br />
            <strong style={{ color:'#1e3a6e' }}>మీ మొబైల్ నంబర్ ఎంటర్ చేయండి</strong>
          </p>
          <form onSubmit={handleSearch}>
            <label style={S.inputLabel}>Parent / Guardian Mobile Number</label>
            <input type="tel" maxLength={10} placeholder="10-digit mobile number" value={mobile}
              onChange={e => setMobile(e.target.value.replace(/\D/g,''))} style={S.input} autoFocus />
            <button type="submit" disabled={loading || mobile.length < 10}
              style={{ ...S.primaryBtn, background: mobile.length >= 10 ? '#1a3a6b' : '#94a3b8', cursor: mobile.length >= 10 ? 'pointer' : 'not-allowed', marginTop: 12 }}>
              {loading ? 'Searching...' : 'View Progress Card'}
            </button>
          </form>
          {error && <div style={S.errorBox}>{error}</div>}
          {results && results.length > 1 && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontWeight:600, color:'#1e3a6e', marginBottom:12, textAlign:'center', fontSize:14 }}>{results.length} students found — Select one:</p>
              {results.map(s => (
                <a key={s.student_id} href={`/progress-card/${encodeURIComponent(schoolCode)}/${s.student_id}`} style={S.studentLink}>
                  <div style={{ fontWeight:700, fontSize:15 }}>{s.student_name}</div>
                  <div style={{ fontSize:13, color:'#64748b', marginTop:2 }}>Class: {s.class}&nbsp;|&nbsp;Father: {s.father_name}{s.admission_no ? `  |  Adm: ${s.admission_no}` : ''}</div>
                </a>
              ))}
            </div>
          )}
        </div>
        <div style={S.lookupFooter}><p style={{ color:'#94a3b8', fontSize:11, margin:0 }}>Powered by School Management System</p></div>
      </div>
    </div>
  );
}

// ── Exam helpers ──────────────────────────────────────────────────────────────
function classifyExam(name) {
  const n = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (/^fa\d|formativ/.test(n)) return 'formative';
  if (/^sa\d|summ?ativ/.test(n)) return 'summative';
  return 'other';
}
function examOrder(name) {
  const n = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  let m = n.match(/^fa(\d+)/);
  if (m) { const num = parseInt(m[1]); return ({ 1:1, 2:2, 3:4, 4:5 })[num] ?? num; }
  m = n.match(/^sa(\d+)/);
  if (m) { const num = parseInt(m[1]); return ({ 1:3, 2:6 })[num] ?? (num * 3); }
  if (/formativ/.test(n)) { m = n.match(/(\d+)/); if (m) { const num=parseInt(m[1]); return ({1:1,2:2,3:4,4:5})[num]??num; } return 50; }
  if (/summ?ativ/.test(n)) { m = n.match(/(\d+)/); if (m) { const num=parseInt(m[1]); return ({1:3,2:6})[num]??(num*3); } return 55; }
  return 99;
}

// ── Signature row ─────────────────────────────────────────────────────────────
function SignatureRow({ classTeacher, principal }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'20px 24px 14px', borderTop:'1px solid #dde3f0', flexWrap:'wrap', gap:12, marginTop:'auto' }}>
      {[
        { label: 'Class Teacher', name: classTeacher },
        { label: 'Head of the Institution', name: principal },
        { label: 'Parent / Guardian', name: null },
      ].map(({ label, name }) => (
        <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1, minWidth:110 }}>
          <div style={{ width:'80%', borderBottom:'1.5px solid #555', marginBottom:6 }}></div>
          <div style={{ fontSize:11, color:'#4a5568', fontWeight:600 }}>{label}</div>
          {name && <div style={{ fontSize:12, fontWeight:700, color:'#1e3a6e', marginTop:2 }}>{name}</div>}
        </div>
      ))}
    </div>
  );
}

// ── PAGE 1: Cover ─────────────────────────────────────────────────────────────
function CoverPage({ school_name, school_logo_url, school_location, school_tagline, student, academic_year }) {
  return (
    <div style={cv.page} className="pc-page-break">
      <div style={cv.header}>
        <SchoolLogo logoUrl={school_logo_url} style={cv.logo} />
        <div style={cv.schoolName}>{school_name}</div>
        {school_tagline && <div style={cv.badge}>{school_tagline}</div>}
        {school_location && <div style={cv.address}>&#x1F4CD; {school_location}</div>}
        <div style={cv.reportBanner}>PROGRESS REPORT</div>
      </div>
      <div style={{ flex: 1 }}></div>
      <div style={cv.studentCard}>
        <div style={cv.sRow}><span style={cv.sLabel}>Name:</span><span style={cv.sValue}>{student.student_name}</span></div>
        <div style={{ display:'flex', gap:32, flexWrap:'wrap' }}>
          <div style={cv.sRow}><span style={cv.sLabel}>Class:</span><span style={cv.sValue}>{student.class}</span></div>
          {academic_year && <div style={cv.sRow}><span style={cv.sLabel}>Year:</span><span style={cv.sValue}>{academic_year}</span></div>}
        </div>
        {student.admission_no && <div style={cv.sRow}><span style={cv.sLabel}>Roll No.:</span><span style={cv.sValue}>{student.admission_no}</span></div>}
      </div>
    </div>
  );
}
const cv = {
  page: { background:'linear-gradient(160deg,#deeaf5 0%,#eef4fb 50%,#e4eef8 100%)', minHeight:'277mm', display:'flex', flexDirection:'column', fontFamily:"'Segoe UI',Arial,sans-serif", overflow:'hidden' },
  header: { background:'linear-gradient(135deg,#1a3a6b 0%,#2756a0 60%,#1a3a6b 100%)', padding:'36px 32px 28px', textAlign:'center', color:'#fff', display:'flex', flexDirection:'column', alignItems:'center', gap:10 },
  logo: { width:90, height:90, objectFit:'contain', background:'#fff', borderRadius:'50%', padding:8, boxShadow:'0 4px 16px rgba(0,0,0,0.25)', marginBottom:4 },
  schoolName: { fontSize:30, fontWeight:900, letterSpacing:2, textTransform:'uppercase', color:'#fff', textShadow:'0 2px 6px rgba(0,0,0,0.3)' },
  badge: { background:'#c0392b', color:'#fff', padding:'4px 20px', borderRadius:4, fontSize:13, fontWeight:700, letterSpacing:1, textTransform:'uppercase' },
  address: { fontSize:13, color:'rgba(255,255,255,0.9)', marginTop:2 },
  reportBanner: { background:'#c0392b', color:'#fff', padding:'8px 40px', borderRadius:4, fontSize:17, fontWeight:800, letterSpacing:3, textTransform:'uppercase', marginTop:10 },
  studentCard: { margin:'0 40px 40px', background:'rgba(255,255,255,0.95)', border:'2px solid #1e3a6e', borderRadius:8, padding:'16px 24px', display:'flex', flexDirection:'column', gap:10 },
  sRow: { display:'flex', alignItems:'baseline', gap:12, borderBottom:'1px dotted #aaa', paddingBottom:6 },
  sLabel: { fontSize:13, fontWeight:600, color:'#374151', minWidth:80, flexShrink:0 },
  sValue: { fontSize:15, fontWeight:700, color:'#1e3a6e', fontFamily:'Georgia,serif', flex:1 },
};

// ── PAGE 2: A word to Parents ─────────────────────────────────────────────────
const PARENT_POINTS = [
  'Parents are requested to go through the progress report carefully and more particularly, the remarks of the teachers and make it convenient to contact the school authorities.',
  'Student should be present for all the tests and examinations. No re-examinations will be conducted under any circumstances.',
  'The progress report is an important document of your child, therefore the parents are informed to go through the report carefully.',
  'Parents are advised to see that their wards attend the classes regularly since 80% of attendance at the primary stage of education and 90% at the secondary stage of education is compulsory.',
  'Progress Reports sent to the parents must be duly signed and returned to the Class Teacher within three days.',
  'Co-Curricular Activities are now included for assessment and these areas are in five points grade scale i.e., 41 to 50 marks (A+), 31 to 40 marks (A), 21 to 30 marks (B), 11 to 20 marks (C), 0 to 10 marks (D).',
  'Parents are informed to take special care in the failed subjects and monitor the child\'s performance time to time with the help of teachers.',
  'We earnestly desire your whole hearted co-operation to help your child to pursue studies with fruitful results.',
];
function ParentsWordPage() {
  return (
    <div style={{ background:'#fff', minHeight:'277mm', padding:'32px 36px', fontFamily:"'Segoe UI',Arial,sans-serif", display:'flex', flexDirection:'column' }} className="pc-page-break">
      <div style={{ textAlign:'center', fontSize:20, fontWeight:800, color:'#c0392b', textDecoration:'underline', marginBottom:20 }}>A word to the Parents</div>
      <ol style={{ paddingLeft:24, lineHeight:1.9, color:'#222', fontSize:13 }}>
        {PARENT_POINTS.map((pt, i) => <li key={i} style={{ marginBottom:10 }}>{pt}</li>)}
      </ol>
      <div style={{ textAlign:'center', fontWeight:700, fontSize:13, color:'#1e3a6e', margin:'24px 0 12px', textTransform:'uppercase', letterSpacing:1 }}>Assessment Grading Scale</div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap', justifyContent:'center' }}>
        {[
          { head:'Formative (20M)', rows:[['18.5–20','A1'],['16.5–18','A2'],['14.5–16','B1'],['12.5–14','B2'],['10.5–12','C1'],['8.5–10','C2'],['7–8','D'],['0–7','E']], cols:['Marks','Grade'] },
          { head:'Summative (100M)', rows:[['91–100','A1','10'],['81–90','A2','9'],['71–80','B1','8'],['61–70','B2','7'],['51–60','C1','6'],['41–50','C2','5'],['35–40','D','4'],['0–34','E','3']], cols:['Marks','Grade','Points'] },
        ].map(({ head, rows, cols }) => (
          <div key={head} style={{ flex:'1 1 200px' }}>
            <div style={{ background:'#1e3a6e', color:'#fff', padding:'6px 8px', textAlign:'center', fontSize:11, fontWeight:700 }}>{head}</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>{cols.map(c => <th key={c} style={{ background:'#2756a0', color:'#fff', padding:'5px 8px', textAlign:'center', fontSize:11, border:'1px solid #c8d3e8' }}>{c}</th>)}</tr></thead>
              <tbody>{rows.map(r => <tr key={r[0]}>{r.map((cell, ci) => <td key={ci} style={{ padding:'4px 8px', textAlign:'center', border:'1px solid #dde3f0', background: ci===1?'#f0f4f8':'#f9fafb', fontWeight: ci===1?700:400, color: ci===1?'#1e3a6e':'#222' }}>{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PAGE 3: Student Info ──────────────────────────────────────────────────────
function StudentInfoPage({ student, academic_year, principal_name, class_teacher_name }) {
  const yr = academic_year || '';
  const [y1, y2] = yr.includes('-') ? yr.split('-') : [yr, yr];
  const dob = student.dob ? new Date(student.dob).toLocaleDateString('en-IN') : '—';

  const Field = ({ label, value, span }) => (
    <div style={{ display:'flex', alignItems:'baseline', gap:8, padding:'5px 0', borderBottom:'1px dotted #ccc', ...(span ? { gridColumn:'1 / -1' } : {}) }}>
      <span style={{ fontSize:12, fontWeight:600, color:'#374151', minWidth:155, flexShrink:0 }}>{label} :</span>
      <span style={{ fontSize:13, fontWeight:700, color:'#1e3a6e', flex:1, borderBottom:'1px dotted #bbb', paddingLeft:4 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ background:'#f0f4f8', minHeight:'277mm', padding:'24px', fontFamily:"'Segoe UI',Arial,sans-serif", display:'flex', flexDirection:'column' }} className="pc-page-break">
      <div style={{ background:'#fff', border:'2px solid #1e3a6e', borderRadius:4, padding:'24px 28px', flex:1, display:'flex', flexDirection:'column' }}>
        <div style={{ textAlign:'center', fontSize:22, fontWeight:800, color:'#1e3a6e', marginBottom:4 }}>Progress Report</div>
        <div style={{ textAlign:'center', fontSize:16, fontWeight:600, color:'#4a5568', marginBottom:20 }}>{y1} — {y2}</div>
        <div style={{ width:100, height:120, border:'1.5px solid #aaa', margin:'0 auto 20px', background:'#f9fafb' }}></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
          <Field label="Name of the Student" value={student.student_name} span />
          <Field label="Name of the Father" value={student.father_name} span />
          <Field label="Name of the Mother" value={student.mother_name} span />
          <Field label="Name of the Guardian" value={student.guardian_name} span />
          <Field label="Date of Birth" value={dob} span />
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:16, padding:'5px 0', borderBottom:'1px dotted #ccc', flexWrap:'wrap' }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#374151' }}>Class &amp; Sec :</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1e3a6e', borderBottom:'1px dotted #bbb', minWidth:60 }}>{student.class}</span>
            <span style={{ fontSize:12, fontWeight:600, color:'#374151', marginLeft:12 }}>Roll No :</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1e3a6e', borderBottom:'1px dotted #bbb', minWidth:40 }}>{student.admission_no || '—'}</span>
            <span style={{ fontSize:12, fontWeight:600, color:'#374151', marginLeft:12 }}>Admn. No :</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1e3a6e', borderBottom:'1px dotted #bbb', minWidth:60 }}>{student.admission_no || '—'}</span>
          </div>
          <Field label="I Language" value={student.lang1} />
          <Field label="II Language" value={student.lang2} />
          <Field label="Address" value={student.village} span />
          <Field label="Aadhaar No." value={student.aadhar_id} span />
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:16, padding:'5px 0', borderBottom:'1px dotted #ccc', flexWrap:'wrap' }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#374151' }}>Ph (Res) :</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1e3a6e', borderBottom:'1px dotted #bbb', flex:1, minWidth:80 }}>{student.mobile || '—'}</span>
            <span style={{ fontSize:12, fontWeight:600, color:'#374151', marginLeft:12 }}>Ph (Off.) :</span>
            <span style={{ fontSize:13, color:'#1e3a6e', borderBottom:'1px dotted #bbb', flex:1, minWidth:80 }}>&nbsp;</span>
          </div>
        </div>
        <div style={{ textAlign:'center', fontSize:12, fontWeight:600, color:'#555', marginTop:24, marginBottom:12 }}>Specimen Signature of the Parent / Guardian</div>
        <div style={{ display:'flex', flexDirection:'column', gap:16, alignItems:'center' }}>
          <div style={{ width:200, borderBottom:'1px solid #aaa' }}></div>
          <div style={{ width:200, borderBottom:'1px solid #aaa' }}></div>
        </div>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', padding:'20px 28px 0', marginTop:16 }}>
        {[{ label:'Class Teacher', name:class_teacher_name }, { label:'Principal / H.M.', name:principal_name }].map(({ label, name }) => (
          <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
            <div style={{ width:140, borderBottom:'1.5px solid #555', marginBottom:6 }}></div>
            <div style={{ fontSize:11, color:'#4a5568', fontWeight:600 }}>{label}</div>
            {name && <div style={{ fontSize:12, fontWeight:700, color:'#1e3a6e', marginTop:2 }}>{name}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PAGE FA: Formative Assessment ─────────────────────────────────────────────
const TRAITS = ['Participation', 'Attitude', 'Sociability', 'Discipline', 'Conversation'];
const TRAIT_LEVELS = ['Excellent', 'Good', 'Average', 'Below Average'];

function FAPage({ exam, faNumber, subjects, examSubjects, results, attendance, principal_name, class_teacher_name, faState, onFaChange }) {
  const examSubs = examSubjects[exam.exam_id] || [];
  const subjectsInExam = subjects.filter(s => examSubs.some(es => es.subject_name === s));
  let totalObt = 0, totalMax = 0;
  const rows = subjectsInExam.map(subject => {
    const r = results[exam.exam_id]?.[subject];
    const es = examSubs.find(s => s.subject_name === subject);
    const maxM = r ? (r.max_marks || es?.max_marks || 20) : (es?.max_marks || 20);
    const obtained = r ? parseFloat(r.marks_obtained) : null;
    totalMax += maxM;
    if (obtained !== null) totalObt += obtained;
    const pct = obtained !== null && maxM > 0 ? (obtained / maxM * 100) : null;
    return { subject, obtained, maxM, gi: pct !== null ? vedaGrade(pct) : null };
  });
  const totalGi = totalMax > 0 ? vedaGrade((totalObt / totalMax) * 100) : null;
  const traits = faState?.traits || {};
  const remarks = faState?.remarks || '';
  const attPct = attendance?.total_days > 0
    ? ((attendance.present_days + (attendance.half_days || 0) * 0.5) / attendance.total_days * 100).toFixed(1) : null;
  const ROMAN = ['I','II','III','IV'];

  return (
    <div style={P.page} className="pc-page-break">
      <div style={P.title}>FORMATIVE ASSESSMENT — {ROMAN[faNumber - 1] || faNumber}</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={{ ...P.th, ...P.thSub }}>Subject</th>
            <th style={P.th}>Marks Obtained</th>
            <th style={P.th}>Max Marks</th>
            <th style={P.th}>Total</th>
            <th style={P.th}>GPA</th>
            <th style={P.th}>Grade</th>
          </tr></thead>
          <tbody>
            {rows.map(({ subject, obtained, maxM, gi }, si) => (
              <tr key={subject} style={{ background: si % 2 === 0 ? '#f9fafb' : '#fff' }}>
                <td style={P.tdSub}>{subject}</td>
                <td style={P.td}>{obtained !== null ? obtained : 'AB'}</td>
                <td style={P.td}>{maxM}</td>
                <td style={{ ...P.td, fontWeight:700, color:'#1e3a6e' }}>{obtained !== null ? obtained : '—'}</td>
                <td style={P.td}>{gi ? gi.gpa : '—'}</td>
                <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e' }}>{gi ? gi.grade : '—'}</td>
              </tr>
            ))}
            <tr style={P.totRow}>
              <td style={{ ...P.tdSub, fontWeight:700 }}>Total / Grade</td>
              <td style={P.td}></td><td style={P.td}></td>
              <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e', fontSize:14 }}>{totalObt}</td>
              <td style={P.td}></td>
              <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e' }}>{totalGi ? totalGi.grade : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={P.secTitle}>Overall Performance</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={{ ...P.th, ...P.thSub }}>Traits</th>
            {TRAIT_LEVELS.map(l => <th key={l} style={P.th}>{l}</th>)}
          </tr></thead>
          <tbody>
            {TRAITS.map((trait, ti) => (
              <tr key={trait} style={{ background: ti % 2 === 0 ? '#f9fafb' : '#fff' }}>
                <td style={P.tdSub}>{trait}</td>
                {TRAIT_LEVELS.map(level => (
                  <td key={level} style={{ ...P.td, cursor:'pointer' }}
                    onClick={() => onFaChange({ ...faState, traits: { ...traits, [trait]: traits[trait] === level ? '' : level } })}>
                    <span className="pc-no-print" style={{ display:'inline-block', width:18, height:18, borderRadius:3, border:`2px solid ${traits[trait]===level?'#1e3a6e':'#ccc'}`, background:traits[trait]===level?'#1e3a6e':'#fff', cursor:'pointer', verticalAlign:'middle' }}></span>
                    <span className="pc-print-only" style={{ fontSize:16 }}>{traits[trait]===level?'✓':''}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={P.secTitle}>Attendance</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={P.th}>Total Working Days</th>
            <th style={P.th}>Days Attended</th>
            <th style={{ ...P.th, width:'40%' }}>Remarks</th>
          </tr></thead>
          <tbody><tr>
            <td style={{ ...P.td, fontSize:15, fontWeight:700 }}>{attendance?.total_days ?? '—'}</td>
            <td style={{ ...P.td, fontSize:15, fontWeight:700 }}>
              {attendance?.present_days ?? '—'}{attendance?.half_days > 0 ? ` (+${attendance.half_days}½)` : ''}
            </td>
            <td style={{ ...P.td, textAlign:'left', verticalAlign:'top', padding:'8px' }}>
              <textarea className="pc-no-print" value={remarks}
                onChange={e => onFaChange({ ...faState, remarks: e.target.value })}
                placeholder="Enter remarks..." style={P.textarea} />
              <span className="pc-print-only" style={{ fontSize:13, fontWeight:600, color:'#1e3a6e' }}>{remarks}</span>
              {attPct !== null && <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>Attendance: {attPct}%</div>}
            </td>
          </tr></tbody>
        </table>
      </div>

      <div style={{ flex:1 }}></div>
      <SignatureRow classTeacher={class_teacher_name} principal={principal_name} />
    </div>
  );
}

// ── PAGE SA: Summative Assessment ─────────────────────────────────────────────
const CO_CURRICULAR = [
  'Value Education & Life Skills',
  'Arts & Cultural Education',
  'Health & Physical Education',
  'Work & Computer Education',
];

function SAPage({ saExam, faExams, saNumber, subjects, examSubjects, results, attendance, principal_name, class_teacher_name, saState, onSaChange }) {
  const inExam = (exam, sub) => exam && (examSubjects[exam.exam_id] || []).some(s => s.subject_name === sub);
  const getObt = (exam, sub) => { const r = results[exam.exam_id]?.[sub]; return r != null ? parseFloat(r.marks_obtained) : null; };
  const getSaMax = (sub) => { const r = results[saExam.exam_id]?.[sub]; const es = (examSubjects[saExam.exam_id]||[]).find(s=>s.subject_name===sub); return r?.max_marks||es?.max_marks||80; };
  const termSubs = subjects.filter(s => inExam(saExam,s) || faExams.some(fa=>inExam(fa,s)));
  let totSa=0, totFa=0, totAll=0, counted=0;
  const rows = termSubs.map(subject => {
    const saM = getObt(saExam, subject);
    const faMks = faExams.map(fa => inExam(fa,subject) ? getObt(fa,subject) : null);
    const valid = faMks.filter(m=>m!==null);
    const faAvg = valid.length > 0 ? parseFloat((valid.reduce((a,b)=>a+b,0)/valid.length).toFixed(2)) : null;
    const total = faAvg !== null && saM !== null ? parseFloat((faAvg+saM).toFixed(2)) : (saM !== null ? saM : null);
    const saMaxM = getSaMax(subject);
    const pct = total !== null ? (faAvg !== null ? total : (saM/saMaxM*100)) : null;
    const gi = pct !== null ? vedaGrade(pct) : null;
    if (total !== null) { totSa+=saM||0; totFa+=faAvg||0; totAll+=total; counted++; }
    return { subject, saM, faAvg, total, gi };
  });
  const totalGi = counted > 0 ? vedaGrade(totAll / counted) : null;
  const coCurricular = saState?.coCurricular || {};
  const remarks = saState?.remarks || '';
  const attPct = attendance?.total_days > 0
    ? ((attendance.present_days+(attendance.half_days||0)*0.5)/attendance.total_days*100).toFixed(1) : null;
  const faLabel = faExams.length > 0 ? faExams.map(e=>e.exam_name).join('+') + ' (20%)' : 'FA Avg (20%)';
  const ROMAN = ['I','II'];

  return (
    <div style={P.page} className="pc-page-break">
      <div style={P.title}>SUMMATIVE ASSESSMENT — {ROMAN[saNumber-1] || saNumber}</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={{ ...P.th, ...P.thSub }}>Subject</th>
            <th style={P.th}>Exam (80)</th>
            <th style={P.th}>{faLabel}</th>
            <th style={P.th}>Total (100%)</th>
            <th style={P.th}>Grade</th>
            <th style={P.th}>GPA</th>
          </tr></thead>
          <tbody>
            {rows.map(({ subject, saM, faAvg, total, gi }, si) => (
              <tr key={subject} style={{ background: si%2===0?'#f9fafb':'#fff' }}>
                <td style={P.tdSub}>{subject}</td>
                <td style={P.td}>{saM!==null?saM:'—'}</td>
                <td style={{ ...P.td, color:'#1e3a6e' }}>{faAvg!==null?faAvg.toFixed(1):'—'}</td>
                <td style={{ ...P.td, fontWeight:700, color:'#1e3a6e' }}>{total!==null?total.toFixed(1):'—'}</td>
                <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e' }}>{gi?gi.grade:'—'}</td>
                <td style={P.td}>{gi?gi.gpa:'—'}</td>
              </tr>
            ))}
            <tr style={P.totRow}>
              <td style={{ ...P.tdSub, fontWeight:700 }}>Total / Grade</td>
              <td style={P.td}></td><td style={P.td}></td>
              <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e', fontSize:14 }}>{totAll.toFixed(1)}</td>
              <td style={{ ...P.td, fontWeight:800, color:'#1e3a6e' }}>{totalGi?totalGi.grade:'—'}</td>
              <td style={P.td}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={P.secTitle}>Co-Curricular Activities</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={{ ...P.th, ...P.thSub }}>Activity</th>
            <th style={P.th}>Marks (50)</th>
            <th style={P.th}>Grade</th>
          </tr></thead>
          <tbody>
            {CO_CURRICULAR.map((act, ai) => {
              const coData = coCurricular[act] || {};
              const updateCo = (field, val) => onSaChange({ ...saState, coCurricular: { ...coCurricular, [act]: { ...coData, [field]: val } } });
              return (
                <tr key={act} style={{ background: ai%2===0?'#f9fafb':'#fff' }}>
                  <td style={P.tdSub}>{act}</td>
                  <td style={P.td}>
                    <input className="pc-no-print" type="number" min="0" max="50" value={coData.marks||''} onChange={e=>updateCo('marks',e.target.value)} style={P.coInput} placeholder="—" />
                    <span className="pc-print-only">{coData.marks||''}</span>
                  </td>
                  <td style={P.td}>
                    <input className="pc-no-print" type="text" value={coData.grade||''} onChange={e=>updateCo('grade',e.target.value)} style={{ ...P.coInput, maxWidth:60 }} placeholder="—" />
                    <span className="pc-print-only">{coData.grade||''}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={P.secTitle}>Attendance</div>
      <div style={P.tableWrap}>
        <table style={P.table}>
          <thead><tr>
            <th style={P.th}>Total Working Days</th>
            <th style={P.th}>Days Attended</th>
            <th style={{ ...P.th, width:'40%' }}>Remarks</th>
          </tr></thead>
          <tbody><tr>
            <td style={{ ...P.td, fontSize:15, fontWeight:700 }}>{attendance?.total_days??'—'}</td>
            <td style={{ ...P.td, fontSize:15, fontWeight:700 }}>
              {attendance?.present_days??'—'}{attendance?.half_days>0?` (+${attendance.half_days}½)`:''}
            </td>
            <td style={{ ...P.td, textAlign:'left', verticalAlign:'top', padding:'8px' }}>
              <textarea className="pc-no-print" value={remarks} onChange={e=>onSaChange({...saState,remarks:e.target.value})} placeholder="Enter remarks..." style={P.textarea} />
              <span className="pc-print-only" style={{ fontSize:13, fontWeight:600, color:'#1e3a6e' }}>{remarks}</span>
              {attPct!==null && <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>Attendance: {attPct}%</div>}
            </td>
          </tr></tbody>
        </table>
      </div>

      <div style={{ flex:1 }}></div>
      <SignatureRow classTeacher={class_teacher_name} principal={principal_name} />
    </div>
  );
}

// ── Shared page styles ────────────────────────────────────────────────────────
const P = {
  page: { background:'#fff', minHeight:'277mm', padding:'20px 24px', fontFamily:"'Segoe UI',Arial,sans-serif", display:'flex', flexDirection:'column' },
  title: { background:'#1e3a6e', color:'#fff', textAlign:'center', padding:'12px', fontSize:16, fontWeight:800, letterSpacing:2, textTransform:'uppercase', marginBottom:12 },
  secTitle: { background:'#eef2f8', borderTop:'2px solid #1e3a6e', borderBottom:'1px solid #dde3f0', padding:'7px 16px', fontWeight:700, fontSize:13, color:'#1e3a6e', marginTop:10, letterSpacing:0.3 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th: { background:'#1e3a6e', color:'#fff', padding:'9px 8px', textAlign:'center', border:'1px solid #c8d3e8', fontSize:12, fontWeight:700 },
  thSub: { textAlign:'left', minWidth:140 },
  tdSub: { padding:'8px', border:'1px solid #e5e7eb', fontWeight:500, color:'#1f2937' },
  td: { padding:'8px', border:'1px solid #e5e7eb', textAlign:'center', color:'#374151' },
  totRow: { background:'#eef2f8', borderTop:'2px solid #1e3a6e' },
  textarea: { width:'100%', border:'1px solid #dde3f0', borderRadius:4, padding:'6px 8px', fontSize:12, minHeight:50, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' },
  coInput: { border:'1px solid #dde3f0', borderRadius:4, padding:'4px 6px', fontSize:12, width:'100%', textAlign:'center', boxSizing:'border-box' },
};

// ── StudentProgressCard ───────────────────────────────────────────────────────
function StudentProgressCard({ schoolCode, studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [faStateMap, setFaStateMap] = useState({});
  const [saStateMap, setSaStateMap] = useState({});
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saved'

  const storageKey = `geetham_pc_${schoolCode}_${studentId}`;

  useEffect(() => {
    if (!schoolCode || !studentId) { setError('Invalid progress card URL'); setLoading(false); return; }
    const params = new URLSearchParams(window.location.search);
    const year = params.get('year') || '';
    axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/${studentId}${year?`?year=${year}`:''}`)
      .then(res => {
        if (res.data.success) {
          setData(res.data.data);
          try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
              const { faStateMap: savedFa, saStateMap: savedSa } = JSON.parse(saved);
              if (savedFa) setFaStateMap(savedFa);
              if (savedSa) setSaStateMap(savedSa);
            }
          } catch {}
        } else setError(res.data.message||'Failed to load');
      })
      .catch(err => setError(err.response?.data?.message||'Failed to load progress card'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={S.center}>
      <style>{`@keyframes pc-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ ...S.spinner, animation:'pc-spin 1s linear infinite' }} />
      <p style={{ color:'#4a5568', marginTop:14 }}>Loading Progress Card...</p>
    </div>
  );
  if (error) return <div style={S.center}><p style={{ color:'#c0392b', fontSize:18 }}>{error}</p></div>;
  if (!data) return null;

  const handleSave = (faMap, saMap) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ faStateMap: faMap, saStateMap: saMap }));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2500);
    } catch {}
  };

  const { student, school_name, school_logo_url, academic_year, exams, subjects, examSubjects, results, attendance, principal_name, class_teacher_name } = data;

  const sortedExams = [...exams].sort((a, b) => {
    const oA = examOrder(a.exam_name), oB = examOrder(b.exam_name);
    return oA !== oB ? oA - oB : a.exam_id - b.exam_id;
  });

  const termGroups = [];
  let pendingFAs = [];
  for (const exam of sortedExams) {
    if (classifyExam(exam.exam_name) === 'summative') { termGroups.push({ saExam: exam, faExams: pendingFAs }); pendingFAs = []; }
    else pendingFAs.push(exam);
  }
  const standaloneExams = pendingFAs;

  // Assign sequential FA/SA numbers across all exams
  let faNum = 0, saNum = 0;
  const faNumMap = {}, saNumMap = {};
  for (const exam of sortedExams) {
    if (classifyExam(exam.exam_name) !== 'summative') faNumMap[exam.exam_id] = ++faNum;
    else saNumMap[exam.exam_id] = ++saNum;
  }

  const commonProps = { subjects, examSubjects, results, attendance, principal_name, class_teacher_name };

  return (
    <div style={S.page} className="progress-card-page">
      <style>{multiPagePrintStyles}</style>

      <CoverPage
        school_name={school_name} school_logo_url={school_logo_url}
        school_location={student.school_location} school_tagline={student.school_tagline}
        student={student} academic_year={academic_year}
      />
      <ParentsWordPage />
      <StudentInfoPage student={student} academic_year={academic_year} principal_name={principal_name} class_teacher_name={class_teacher_name} />

      {termGroups.map(({ saExam, faExams }) => (
        <React.Fragment key={saExam.exam_id}>
          {faExams.map(exam => (
            <FAPage key={exam.exam_id} exam={exam} faNumber={faNumMap[exam.exam_id]||1}
              {...commonProps}
              faState={faStateMap[exam.exam_id]}
              onFaChange={val => { const next = { ...faStateMap, [exam.exam_id]: val }; setFaStateMap(next); }}
            />
          ))}
          <SAPage saExam={saExam} faExams={faExams} saNumber={saNumMap[saExam.exam_id]||1}
            {...commonProps}
            saState={saStateMap[saExam.exam_id]}
            onSaChange={val => { const next = { ...saStateMap, [saExam.exam_id]: val }; setSaStateMap(next); }}
          />
        </React.Fragment>
      ))}

      {standaloneExams.map(exam => (
        <FAPage key={exam.exam_id} exam={exam} faNumber={faNumMap[exam.exam_id]||1}
          {...commonProps}
          faState={faStateMap[exam.exam_id]}
          onFaChange={val => { const next = { ...faStateMap, [exam.exam_id]: val }; setFaStateMap(next); }}
        />
      ))}

      <div className="pc-no-print" style={{ textAlign:'center', padding:'24px 0', display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
        <button style={S.saveBtn} onClick={() => handleSave(faStateMap, saStateMap)}>
          {saveStatus === 'saved' ? '✓ Saved!' : '💾 Save'}
        </button>
        <button style={S.printBtn} onClick={() => window.print()}>&#x1F5A8; Print Progress Card</button>
        <button style={{ ...S.saveBtn, background:'#166534', minWidth:160 }} onClick={() => { handleSave(faStateMap, saStateMap); setTimeout(() => window.print(), 200); }}>
          💾 Save &amp; Print
        </button>
      </div>
    </div>
  );
}

// ── Print CSS ─────────────────────────────────────────────────────────────────
const printStyles = `@media print { body{background:#fff!important} .pc-no-print{display:none!important} .progress-card-page>div{box-shadow:none!important;border-radius:0!important} }`;

const multiPagePrintStyles = `
@page { size: A4 portrait; margin: 0; }
@media print {
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
  html,body{background:#fff!important;margin:0!important;padding:0!important;width:210mm!important;height:auto!important;overflow:visible!important}
  .pc-no-print{display:none!important}
  .pc-print-only{display:inline!important}
  .progress-card-page{padding:0!important;background:#fff!important;display:block!important;height:auto!important;overflow:visible!important;min-height:unset!important}
  .pc-page-break{page-break-after:always!important;break-after:page!important;margin:0!important;box-shadow:none!important;border-radius:0!important;border:none!important;min-height:277mm!important;width:210mm!important;box-sizing:border-box!important;display:flex!important;flex-direction:column!important}
  .progress-card-page>div:last-child{page-break-after:avoid!important;break-after:avoid!important}
  textarea,input{border:none!important;outline:none!important;background:transparent!important;resize:none!important}
}
@media screen { .pc-print-only{display:none!important} }
`;

// ── Lookup/shared styles ──────────────────────────────────────────────────────
const S = {
  page: { minHeight:'100vh', height:'100vh', overflowY:'auto', background:'#eef2f7', padding:'24px 12px', fontFamily:"'Segoe UI','Arial',sans-serif" },
  center: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh' },
  spinner: { width:42, height:42, border:'4px solid #dde3f0', borderTop:'4px solid #1a3a6b', borderRadius:'50%' },
  card: { maxWidth:920, margin:'0 auto', background:'#fff', borderRadius:14, boxShadow:'0 6px 32px rgba(26,58,107,0.13)', border:'1px solid #dde3f0' },
  header: { background:'linear-gradient(135deg,#1a3a6b 0%,#2756a0 60%,#1a3a6b 100%)', color:'#fff', padding:'28px 32px 0', display:'flex', flexDirection:'column', alignItems:'center', gap:6 },
  headerLogo: { width:80, height:80, objectFit:'contain', background:'#fff', borderRadius:12, padding:6, flexShrink:0, boxShadow:'0 2px 8px rgba(0,0,0,0.18)' },
  schoolName: { margin:0, fontSize:26, fontWeight:800, letterSpacing:0.5, textShadow:'0 1px 3px rgba(0,0,0,0.2)' },
  tagline: { margin:'4px 0 0', fontSize:13, opacity:0.85, fontStyle:'italic' },
  location: { margin:'3px 0 0', fontSize:12, opacity:0.75 },
  cardTitleBanner: { background:'rgba(255,255,255,0.12)', borderTop:'1px solid rgba(255,255,255,0.25)', marginTop:4, padding:'10px 0', display:'flex', alignItems:'center', justifyContent:'center', width:'100%' },
  cardTitleText: { fontSize:15, fontWeight:700, letterSpacing:3, textTransform:'uppercase', opacity:0.97 },
  inputLabel: { display:'block', fontSize:12, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 },
  input: { width:'100%', padding:'14px 16px', fontSize:18, fontWeight:600, border:'2px solid #c8d3e8', borderRadius:10, outline:'none', letterSpacing:3, textAlign:'center', boxSizing:'border-box', color:'#1e3a6e' },
  primaryBtn: { display:'block', width:'100%', padding:'14px', fontSize:15, fontWeight:700, color:'#fff', border:'none', borderRadius:10 },
  errorBox: { marginTop:16, padding:'12px 16px', background:'#fee2e2', borderRadius:8, color:'#991b1b', textAlign:'center', fontSize:14, border:'1px solid #fca5a5' },
  studentLink: { display:'block', padding:'14px 16px', marginBottom:8, background:'#f5f7fb', border:'2px solid #dde3f0', borderRadius:10, textDecoration:'none', color:'#1e3a6e' },
  lookupFooter: { padding:'12px 30px', borderTop:'1px solid #e8edf6', textAlign:'center' },
  printBtn: { background:'#1e3a6e', color:'#fff', border:'none', borderRadius:8, padding:'10px 28px', cursor:'pointer', fontSize:14, fontWeight:600, letterSpacing:0.3 },
  saveBtn: { background:'#15803d', color:'#fff', border:'none', borderRadius:8, padding:'10px 28px', cursor:'pointer', fontSize:14, fontWeight:600, letterSpacing:0.3, minWidth:110 },
};
